// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { login as apiLogin, register as apiRegister, request, ROUTES } from "../services/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "token";

function getStatus(err) {
  // Support axios-like shapes + your request() normalized error patterns
  return err?.response?.status || err?.status || null;
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY) || null;
    } catch {
      return null;
    }
  });

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // IMPORTANT: keep "logged in" based on token presence
  const isAuthenticated = Boolean(token);

  // Hydrate current user (backend-ready)
  useEffect(() => {
    let mounted = true;

    async function hydrate() {
      setLoading(true);
      if (!token) {
        if (mounted) {
           setUser(null);
           setLoading(false);
        }
        return;
      }

      try {
        const me = await request({ url: ROUTES.authMe, method: "GET" });
        if (!mounted) return;

        const u = me?.user || me?.data?.user || me?.data || me;
        setUser(u || null);
      } catch (err) {
        if (!mounted) return;

        const status = getStatus(err);

        // ✅ Only clear token when backend says the token is invalid/unauthorized
        if (status === 401 || status === 403) {
          try {
            localStorage.removeItem(TOKEN_KEY);
          } catch {}
          setToken(null);
          setUser(null);
        } else {
          // ✅ For 404/500/network/etc: do NOT log out.
          // Keep token so UI stays authenticated; just leave user null.
          setUser(null);
        }

        // Optional: console warn for debugging
        // console.warn("Auth hydrate failed (kept token):", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    hydrate();

    return () => {
      mounted = false;
    };
  }, [token]);

  const login = async (credentials) => {
    const res = await apiLogin(credentials);

    const t = res?.token || res?.data?.token || null;
    const u = res?.user || res?.data?.user || res?.data || null;

    if (!t) throw new Error("Invalid login response");

    try {
      localStorage.setItem(TOKEN_KEY, t);
    } catch {}

    setToken(t);
    setUser(u || null);

    return { token: t, user: u || null };
  };

  const register = async (payload) => {
    const res = await apiRegister(payload);

    const t = res?.token || res?.data?.token || null;
    const u = res?.user || res?.data?.user || res?.data || null;

    if (t) {
      try {
        localStorage.setItem(TOKEN_KEY, t);
      } catch {}
      setToken(t);
      setUser(u || null);
      return { token: t, user: u || null };
    }

    return res;
  };

  const logout = () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {}
    setToken(null);
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      isAuthenticated,
      login,
      register,
      logout,
      setUser, // optional helper for profile updates
      setToken, // optional helper if you ever refresh token
    }),
    [user, token, loading, isAuthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
